<template>
  <div>
    <BaseDateInput
      :id="id"
      :model-value="props.modelValue"
      :nullable="header.isNullable"
      :include-time="isDateTimeField"
      :read-only="readOnly"
      :single-line="singleLine"
      :error="saveError"
      :date-format="dateFormat"
      @update:model-value="onDateUpdated"
    />
    <BaseProgressBar v-if="saving" />
    <div v-else class="h-1 m-0 p-0" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useTableStore } from '@/store/storeTable';
import { useCellSave } from '@/composables/useCellSave';
import { useUserPrefFormatting } from '@/composables/useUserPrefFormatting';

import BaseDateInput from '@/components/BaseComponents/BaseDateInput.vue';
import BaseProgressBar from '@/components/BaseComponents/BaseProgressBar.vue';

const props = defineProps({
  modelValue: {
    type: [String, Date],
    default: null,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  singleLine: {
    type: Boolean,
    default: false,
  },
  autoSave: {
    type: Boolean,
    default: true,
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  onSaveSuccess: { type: Function, default: null },
  // US 3399: valgfri id som settes paa selve kontrollen, saa en etikett kan knyttes med for=.
  // Uten den erklaeringen ville Vues attribute-fallthrough lagt id-en paa rot-diven, og en
  // for=-peker dit navngir ingenting for en skjermleser selv om statisk analyse blir fornoeyd.
  id: { type: String, default: undefined }
});

const emit = defineEmits(['update:modelValue']);

const tableStore = useTableStore();
const { dateFormat } = useUserPrefFormatting();
const { saving, saveErrorMessage: saveError, saveCell } = useCellSave();

const isDateTimeField = computed(() => props.header.basicDataType === 'datetime2');

async function persist (newValue) {
  let serializedValue = null;
  if (newValue instanceof Date) {
    const isoDateOnly = newValue.toISOString().split('T')[0];
    serializedValue = isDateTimeField.value
      ? `${isoDateOnly}T${newValue.toISOString().split('T')[1]}`
      : isoDateOnly;
  }

  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: serializedValue,
  };

  await saveCell(item, props.onSaveSuccess);
}

async function onDateUpdated (newValue) {
  emit('update:modelValue', newValue);
  if (!props.autoSave) return;
  await persist(newValue);
}
</script>
